import React, { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import Button from "ui/Button";
import SectionHeading from "ui/SectionHeading";

import { OrganizationIntegrationsQuery } from "../../gql/Query.generated";
import {
  TestWebhookDestinationMutation,
  useCreateWebhookEndpointMutation,
  useTestWebhookDestinationMutation,
  useUpdateWebhookEndpointMutation,
} from "../gql/Mutation.generated";
import { WebhookSigningKeyType } from "../SigningKeys";
import SigningSecret from "../SigningSecret";

type WebhookEndpointType =
  OrganizationIntegrationsQuery["getOrganizationDetails"]["webhookEndpoints"][number];

export type TestResultType =
  TestWebhookDestinationMutation["testWebhookDestination"];

type HeaderRow = { name: string; value: string };

const REFETCH_QUERIES = ["OrganizationIntegrations"];

function WebhookForm({
  organizationSlug,
  endpoint,
  signingKeys,
  signingKeyCreationError,
  onSaved,
  onCancel,
}: {
  organizationSlug: string;
  endpoint?: WebhookEndpointType;
  signingKeys: WebhookSigningKeyType[];
  signingKeyCreationError: string | null;
  onSaved: (message: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(endpoint?.name ?? "");
  const [url, setUrl] = useState(endpoint?.url ?? "");
  const [replaceHeaders, setReplaceHeaders] = useState(
    !endpoint || endpoint.headerNames.length === 0,
  );
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  // Errors are reported in the modal: a flash would be behind its overlay
  const [formError, setFormError] = useState<React.ReactNode>(null);
  const reportTestFailure = (message: string, result?: TestResultType) => {
    setFormError(
      <TestOutcome result={result}>
        Test delivery failed: {message}. Changes not saved.
      </TestOutcome>,
    );
  };
  const onError = (error: Error) => setFormError(error.message);
  const [createEndpoint, createResult] = useCreateWebhookEndpointMutation({
    refetchQueries: REFETCH_QUERIES,
    onError,
  });
  const [updateEndpoint, updateResult] = useUpdateWebhookEndpointMutation({
    refetchQueries: REFETCH_QUERIES,
    onError,
  });
  const [testDestination, testResult] = useTestWebhookDestinationMutation({
    onError: (error) => reportTestFailure(error.message),
  });
  const busy =
    createResult.loading || updateResult.loading || testResult.loading;

  const headerRows = headers.filter((h) => h.name !== "");
  // Renaming cannot break delivery; skip test
  const deliveryConfigChanged =
    !endpoint ||
    url.trim() !== endpoint.url ||
    (replaceHeaders &&
      (endpoint.headerNames.length > 0 || headerRows.length > 0));
  const handleSave = async () => {
    setFormError(null);
    const headerVariables = replaceHeaders ? headerRows : undefined;

    if (deliveryConfigChanged) {
      // Nothing is written until a ping succeeds
      const tested = await testDestination({
        variables: {
          organizationSlug,
          url,
          headers: headerVariables,
          webhookEndpointId: endpoint?.id,
        },
      });
      const result = tested.data?.testWebhookDestination;
      if (!result) {
        return;
      }
      if (result.status !== "succeeded") {
        reportTestFailure(testFailureMessage(result), result);
        return;
      }
    }

    const saved = endpoint
      ? await updateEndpoint({
          variables: {
            webhookEndpointId: endpoint.id,
            name,
            url,
            headers: headerVariables,
          },
        })
      : await createEndpoint({
          variables: { organizationSlug, name, url, headers: headerVariables },
        });
    if (!saved.data) {
      return;
    }

    onSaved(
      deliveryConfigChanged
        ? "Webhook endpoint tested and saved"
        : "Webhook endpoint saved",
    );
  };

  const setHeader = (i: number, header: HeaderRow) => {
    setHeaders(headers.map((h, j) => (j === i ? header : h)));
  };

  const saveDisabledReason =
    name.trim() === ""
      ? "Enter a name for this endpoint"
      : url.trim() === ""
      ? "Enter the endpoint URL"
      : undefined;

  const saveLabel = !deliveryConfigChanged
    ? "Save Webhook"
    : endpoint
    ? "Test and Save"
    : "Test and Add";

  return (
    <div className="lg:min-w-[560px]">
      <label className="flex flex-col font-normal">
        <SectionHeading>
          Name <span className="text-[#c00]">*</span>
        </SectionHeading>
        <input
          type="text"
          className="form-control"
          required
          aria-required
          placeholder="e.g. Ops alert router"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
      </label>
      <label className="mt-4 flex flex-col font-normal">
        <SectionHeading>
          URL <span className="text-[#c00]">*</span>
        </SectionHeading>
        <input
          type="url"
          className="form-control"
          required
          aria-required
          placeholder="https://example.com/webhooks/pganalyze"
          value={url}
          onChange={(e) => setUrl(e.currentTarget.value)}
        />
      </label>
      <div className="mt-4">
        <SectionHeading>Request headers (Optional)</SectionHeading>
        <div className="text-[#606771]">
          E.g. an Authorization header your receiver requires. Values are stored
          encrypted and not shown again.
        </div>
        {!replaceHeaders ? (
          <div className="mt-2">
            {endpoint.headerNames.map((headerName) => (
              <div key={headerName} className="mb-1 flex items-center gap-2">
                <input
                  type="text"
                  className="form-control"
                  aria-label={`Header ${headerName} name`}
                  value={headerName}
                  disabled
                />
                <input
                  type="text"
                  className="form-control"
                  aria-label={`Header ${headerName} value (hidden)`}
                  value="••••••••"
                  disabled
                />
              </div>
            ))}
            <Button
              className="mt-1"
              bare
              variant="link"
              onClick={() => setReplaceHeaders(true)}
            >
              Replace headers
            </Button>
          </div>
        ) : (
          <div className="mt-2">
            {headers.map((header, i) => (
              <div key={i} className="mb-1 flex items-center gap-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Name"
                  value={header.name}
                  onChange={(e) =>
                    setHeader(i, { ...header, name: e.currentTarget.value })
                  }
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Value"
                  value={header.value}
                  onChange={(e) =>
                    setHeader(i, { ...header, value: e.currentTarget.value })
                  }
                />
                <Button
                  bare
                  aria-label={`Remove header ${header.name || i + 1}`}
                  className="px-2 text-[#999] hover:text-[#c00]"
                  onClick={() => setHeaders(headers.filter((_, j) => j !== i))}
                >
                  ×
                </Button>
              </div>
            ))}
            <Button
              bare
              variant="link"
              onClick={() => setHeaders([...headers, { name: "", value: "" }])}
            >
              Add header
            </Button>
          </div>
        )}
      </div>
      <div className="mt-4">
        <SectionHeading>Signing secret</SectionHeading>
        <div className="mb-2 text-[#606771]">
          Requests targeting this webhook are signed with your organization's
          signing secret. Configure your receiver to verify the signature with
          this secret before testing.
        </div>
        <CurrentSigningSecret
          signingKeys={signingKeys}
          signingKeyCreationError={signingKeyCreationError}
        />
      </div>
      {formError && (
        <div
          role="alert"
          className="mt-4 w-0 min-w-full break-words text-[#c93a2a]"
        >
          <FontAwesomeIcon className="mr-1" icon={faExclamationTriangle} />
          {formError}
        </div>
      )}
      <div className="mt-4">
        <span
          title={saveDisabledReason}
          className={
            saveDisabledReason
              ? "inline-block cursor-not-allowed"
              : "inline-block"
          }
        >
          <Button
            bare
            disabled={!!saveDisabledReason || busy}
            className={
              saveDisabledReason
                ? "btn btn-success pointer-events-none"
                : "btn btn-success"
            }
            onClick={() => {
              void handleSave();
            }}
          >
            {testResult.loading ? "Sending test…" : saveLabel}
          </Button>
        </span>
        <Button
          bare
          className="text-link hover:text-link-hover ml-3"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

// The secret a receiver should be configured with. Until a key exists, assumes one
// is being created (see useCreateSigningKeyIfAbsent).
function CurrentSigningSecret({
  signingKeys,
  signingKeyCreationError,
}: {
  signingKeys: WebhookSigningKeyType[];
  signingKeyCreationError: string | null;
}) {
  const currentKey = currentSigningKey(signingKeys);
  if (currentKey) {
    return <SigningSecret signingKeyId={currentKey.id} canReveal />;
  }
  if (signingKeyCreationError) {
    return (
      <div role="alert" className="text-[#c93a2a]">
        Could not create a signing secret: {signingKeyCreationError}
      </div>
    );
  }
  return <div className="text-[#606771]">Creating signing secret…</div>;
}

export function currentSigningKey(signingKeys: WebhookSigningKeyType[]) {
  return signingKeys.find((key) => key.retiredAt == null);
}

export function TestOutcome({
  result,
  children,
}: {
  result?: TestResultType;
  children: React.ReactNode;
}) {
  return (
    <>
      <span>{children}</span>
      {result?.responseBodyExcerpt && (
        <pre className="mt-1 mb-0 max-h-[120px] overflow-auto whitespace-pre-wrap break-words text-[12px]">
          {result.responseBodyExcerpt}
        </pre>
      )}
    </>
  );
}

export function testFailureMessage(result: TestResultType) {
  if (result.errorMessage) {
    return result.errorMessage;
  }
  if (result.responseStatus) {
    return `the endpoint responded with HTTP ${result.responseStatus}`;
  }
  return "the test delivery did not reach the endpoint";
}

export default WebhookForm;
